// web/src/components/SuggestionsCard.tsx — this month's suggestions for one live site (portal spec 18).
// Green = done for you. Yellow = "Yes, do it" / "No thanks" (no answer by the shown day means yes, U5). Red = let's talk.
// Hidden until KC has released the month (the API never returns a proposal under review) and once it is live.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { apiErrorStatus } from '../lib/errors';
import type { ProposalItem } from '../lib/types';
import { Icon } from './Icon';
import { toast } from './Toast';

/** The auto-yes day is the end of a Mountain-time day (the API's autoYesAt), so show that day, wherever the client is. */
const autoYesDay = (iso: string) =>
  new Date(iso).toLocaleDateString('en-US', { month: 'long', day: 'numeric', timeZone: 'America/Denver' });

export function SuggestionsCard({ siteId }: { siteId: string }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['proposal', siteId], queryFn: () => api.currentProposal(siteId) });
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['proposal', siteId] });
    qc.invalidateQueries({ queryKey: ['home'] });
  };
  const decide = useMutation({
    mutationFn: (v: { item: ProposalItem; decision: 'approve' | 'decline' }) => api.decideItem(q.data!.id, v.item.id, v.decision),
    onSuccess: refresh,
    onError: (e) => {
      if (apiErrorStatus(e) === 409) { toast({ kind: 'info', message: 'That one was already answered.' }); refresh(); return; }
      toast({ kind: 'error', message: 'That didn’t save. Try again.' });
    },
  });
  const p = q.data;
  if (!p || p.status === 'live') return null;
  const red = p.items.filter((i) => i.class === 'red');
  if (p.status === 'closed') return red.length ? <RedNote items={red} /> : null;
  const green = p.items.filter((i) => i.class === 'green');
  const yellow = p.items.filter((i) => i.class === 'yellow');
  const waiting = yellow.some((i) => i.status === 'pending');

  return (
    <section id={`suggestions-${siteId}`} className="min-w-0 scroll-mt-4 rounded-lg border border-hair bg-surface p-4">
      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
        <h2 className="font-head text-ink">This month’s improvements</h2>
        {p.previewUrl && (
          <a href={p.previewUrl} target="_blank" rel="noreferrer" className="inline-flex min-h-11 items-center gap-1 text-sm text-info">
            See the preview<Icon name="external" size={14} />
          </a>
        )}
      </div>
      {green.length > 0 && (
        <ul className="mt-2 flex flex-col gap-1.5">
          {green.map((i) => (
            <li key={i.id} className="flex min-w-0 gap-2 text-sm text-ink">
              <Icon name="check" size={15} className="mt-0.5 shrink-0 text-run" />
              <span className="min-w-0 break-words">{i.title}</span>
            </li>
          ))}
        </ul>
      )}
      {yellow.length > 0 && (
        <div className="mt-4 flex flex-col gap-3">
          <h3 className="font-head text-sm text-muted">{waiting ? 'Your call' : 'Your answers'}</h3>
          {waiting && p.autoYesOn && (
            <p className="text-xs text-muted">No answer by {autoYesDay(p.autoYesOn)} means yes. We’ll go ahead.</p>
          )}
          {yellow.map((i) => (
            <div key={i.id} className="min-w-0 rounded-md border border-hair p-3">
              <div className="break-words font-medium text-ink">{i.title}</div>
              <p className="mt-1 break-words text-sm text-muted">{i.why}</p>
              {i.status === 'pending' ? (
                <div className="mt-3 flex gap-2">
                  <button type="button" disabled={decide.isPending} onClick={() => decide.mutate({ item: i, decision: 'approve' })}
                    className="inline-flex min-h-11 flex-1 items-center justify-center rounded-md bg-run px-4 font-medium text-runink disabled:opacity-50">Yes, do it</button>
                  <button type="button" disabled={decide.isPending} onClick={() => decide.mutate({ item: i, decision: 'decline' })}
                    className="inline-flex min-h-11 flex-1 items-center justify-center rounded-md border border-hair px-4 text-sm text-ink disabled:opacity-50">No thanks</button>
                </div>
              ) : (
                <p className="mt-2 text-xs text-muted">
                  {i.status === 'approved' ? 'Yes — going live.' : i.status === 'declined' ? 'You said no thanks.' : 'Set aside for now.'}
                </p>
              )}
            </div>
          ))}
        </div>
      )}
      {red.length > 0 && <div className="mt-4"><RedNote items={red} bare /></div>}
      {!waiting && (p.status === 'decided' || p.status === 'publishing') && (
        <p className="mt-3 text-sm text-run">Thanks — we’re putting this month’s update live.</p>
      )}
    </section>
  );
}

function RedNote({ items, bare }: { items: ProposalItem[]; bare?: boolean }) {
  const body = (
    <div className="flex flex-col gap-1 rounded-md bg-surface2 p-3 text-sm text-muted">
      {items.map((i) => <p key={i.id} className="break-words"><b className="text-ink">{i.title.replace(/[.!?]+$/, '')}.</b> {i.why}</p>)}
      <p>We’d like to talk this through with you — reply to our email or write to hello@yourproject.ai.</p>
    </div>
  );
  return bare ? body : <section className="min-w-0 rounded-lg border border-hair bg-surface p-4">{body}</section>;
}
